import { createAction } from "@reduxjs/toolkit";
import { consoleInfo, consoleWarn, consoleWarnResponse } from "common/utilities/browser/consoleLog/consoleLog";
import * as API from "core/api/api";
import { isEmptyResponse, isPageResponse, isRedirectResponse, isSectionResponse } from "core/api/api.typesGuards";
import { isRequestAborted, StatusCode } from "core/api/apiHelpers";
import type * as I from "core/api/coreApiClient";
import { isResponse } from "core/api/openApi.typesGuards";
import { commandFailed, getCommand, handleCommandError } from "core/redux/actions/command/commandActions";
import type { DownloadPlasmidView } from "core/redux/reducers/page/pageReducer.types";
import type { AppThunk } from "core/redux/store/configureStore.types";

import type { FetchPageActionOutput, FetchSectionActionOutput } from "./pageActions.types";

export const loadingPage = createAction("page/loadingPage");

export const pageLoaded = createAction("page/pageLoaded", (responseStatus: number, page?: I.Page) => ({
  payload: {
    responseStatus,
    page,
  },
}));

export const pageLoadingFailed = createAction("page/pageLoadingFailed", commandFailed);

export const fetchPage =
  (url: string): AppThunk<FetchPageActionOutput> =>
  async (dispatch) => {
    consoleInfo(`PAGE - Loading: ${url}`);
    dispatch(loadingPage());

    try {
      const response = await API.commandGet(url);

      if (isPageResponse(response)) {
        consoleInfo(`PAGE - Loaded: ${StatusCode.OK}`);

        dispatch(pageLoaded(StatusCode.OK, response.payload));
        return await Promise.resolve(response.payload);
      }

      consoleInfo("PAGE - Loaded with unhandled format");
      return await Promise.reject(response);
    } catch (error) {
      if (await handleCommandError(error, "fetchPage", dispatch, pageLoadingFailed)) {
        return Promise.resolve(undefined);
      }

      consoleWarnResponse("PAGE - Loading error:", url, error);

      const errorResponse = error;

      if (
        isResponse(errorResponse) &&
        !(
          errorResponse.status === StatusCode.REDIRECT ||
          (errorResponse.status === StatusCode.UNPROCESSABLE_ENTITY &&
            isRedirectResponse((await errorResponse.clone().json()) as I.GenericResponse))
        )
      ) {
        dispatch(pageLoadingFailed(errorResponse.statusText, undefined, { status: errorResponse.status }));
      }

      return await Promise.reject();
    }
  };

export const fetchingSection = createAction("page/fetchingSection", (section: I.Section) => ({
  payload: { section },
}));

export const sectionLoaded = createAction(
  "page/sectionLoaded",
  (section: I.Section | undefined, hide: boolean | undefined, toggledVisibility?: boolean) => ({
    payload: {
      section,
      hide: hide !== undefined,
      toggledVisibility,
    },
  })
);

export const sectionLoadingFailed = createAction("page/sectionLoadingFailed", (section: I.Section) => ({
  payload: { section },
}));

export const fetchSection =
  (section: I.Section, url: string): AppThunk<FetchSectionActionOutput> =>
  async (dispatch): Promise<I.Section | undefined> => {
    consoleInfo(`SECTION - loading ${url}`);
    dispatch(fetchingSection(section));

    try {
      const response = await API.commandGet(url, true);

      if (isSectionResponse(response)) {
        consoleInfo(`SECTION - Loaded: ${url}`);

        dispatch(sectionLoaded(response.payload, undefined));
        return await Promise.resolve(response.payload as I.Section);
      }
      if (isEmptyResponse(response)) {
        consoleInfo(`SECTION - Loaded (empty): ${url}`);

        const newSection = { ...section, fetchSectionCommand: undefined };
        dispatch(sectionLoaded(newSection, true));
        return await Promise.resolve(newSection);
      }

      consoleWarn("SECTION - Loaded with unhandled format");
      dispatch(sectionLoaded({ ...section, fetchSectionCommand: undefined }, true));
      return await Promise.reject();
    } catch (errorResponse) {
      if (isRequestAborted(errorResponse)) {
        consoleWarnResponse("SECTION - Loading aborted:", url, errorResponse as Response);
        return await Promise.reject();
      }
      consoleWarnResponse("SECTION - Loading error:", url, errorResponse as Response);

      dispatch(sectionLoadingFailed(section));
      return await Promise.reject();
    }
  };

export const showEmptySections = createAction("page/showEmptySections");
export const hideEmptySections = createAction("page/hideEmptySections");

export const pinLeftSidebar = createAction("page/pinLeftSidebar");
export const unpinLeftSidebar = createAction("page/unpinLeftSidebar");

export const changeSectionVisibility =
  (section: I.Section, command: I.Command): AppThunk<FetchSectionActionOutput> =>
  async (dispatch): Promise<I.Section | undefined> => {
    dispatch(fetchingSection(section));

    try {
      const response = await dispatch(getCommand(command));

      if (isSectionResponse(response)) {
        dispatch(sectionLoaded(response.payload, undefined, true));
        return await Promise.resolve(response.payload);
      }

      dispatch(sectionLoaded(section, true));
      return await Promise.reject();
    } catch (e) {
      dispatch(sectionLoadingFailed(section));
      return await Promise.reject();
    }
  };

export const sectionTableProcessing = createAction(
  "page/sectionTableProcessing",
  (sectionId: string, tableProcessing: boolean) => ({
    payload: { sectionId, tableProcessing },
  })
);

export const stopProcessingOnBlur = createAction("page/edit/stopProcessingOnBlur");
export const clearProcessingOnBlur = createAction("page/edit/clearProcessingOnBlur");

export const plasmidViewerSelection = createAction("page/plasmidViewerSelection", (selection: string[]) => ({
  payload: {
    selection,
  },
}));

export const featuresTableSelection = createAction("page/featuresTableSelection", (selection: I.RowSelection) => ({
  payload: {
    selection,
  },
}));

export const expandSection = createAction("page/expandSection", (sectionId?: string) => ({
  payload: {
    sectionId,
  },
}));

export const downloadPlasmidView = createAction("page/downloadPlasmidView", (data: DownloadPlasmidView) => ({
  payload: data,
}));

export const clearDownloadPlasmidView = createAction("page/clearDownloadPlasmidView");
